/**
 * IncidentsToClose (13.2-08; D-13.2-A-1, D-13.2-P-8): Verify's "Incidents to close", for Oversight only.
 *
 * Each incident a Care Manager has readied (Evaluating) is one quiet card: its ref and title, its unit, who readied
 * it and when, and its checklist (13.2-06's `incidentChecklist`, the same one the closure panel shows and the approve
 * gate refuses on). "Open to close" goes to the record's closure panel (`/record/INC-n#panel-closure`). A card the
 * viewer readied says someone else approves it (D-13-L-19). With nothing readied, it says so plainly.
 *
 * It draws `useIncidentsToClose`'s result and reads nothing itself. For anyone but Oversight it renders nothing.
 */

import { Button } from 'react-aria-components';
import { Link } from 'react-router-dom';
import IncidentChecks from '../../components/record/IncidentChecks';
import { isOversight } from '../../lib/actionAuthority';
import { formatInstant } from '../../lib/fieldValues';
import type { IncidentToClose, UseIncidentsToCloseResult } from '../../hooks/useIncidentsToClose';
import type { Actor } from '../../types/actionRegister';
import styles from './IncidentsToClose.module.css';

export const SELF_READIED_TEXT = 'Someone else approves this (you readied it).';
export const READIER_UNKNOWN_TEXT = "Who readied it isn't on its trail, so it can't be approved yet. Check its trail on the record.";

interface Props {
  result: UseIncidentsToCloseResult;
  actor: Actor | null;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export default function IncidentsToClose({ result, actor }: Props) {
  if (!actor || !isOversight(actor) || result.status === 'idle') return null;
  const { status, items } = result;
  const ready = result.readyCount ?? 0;

  return (
    <section className={styles.section} aria-labelledby="itc-h">
      <div className={styles.sectionHead}>
        <h2 id="itc-h">Incidents to close</h2>
        {status === 'ready' && items.length > 0 && (
          <span className={styles.count}>
            <b>{ready}</b> of {items.length} ready for you
          </span>
        )}
      </div>
      <p className={styles.lede}>
        Readied by a Care Manager. Check reporting and follow-ups here, then open each one to review and close it.
      </p>

      {status === 'loading' && <p className={styles.quietLine}>Reading the incidents waiting to close…</p>}

      {status === 'error' && (
        <div className={styles.problem} role="alert">
          <p>
            <b>The incidents to close couldn't be read.</b> {result.error}
          </p>
          <Button className={styles.retry} onPress={result.reload}>
            Try again
          </Button>
        </div>
      )}

      {status === 'ready' && (result.followUpsNote || result.readiedNote) && (
        <div className={styles.problem} role="status">
          {result.followUpsNote && <p>{result.followUpsNote}</p>}
          {result.readiedNote && <p>{result.readiedNote}</p>}
          <Button className={styles.retry} onPress={result.reload}>
            Try again
          </Button>
        </div>
      )}

      {status === 'ready' && items.length === 0 && (
        <p className={styles.clear}>
          <b>Nothing to close.</b> When a Care Manager readies an incident for approval, it lands here.
        </p>
      )}

      {status === 'ready' && items.length > 0 && (
        <ul className={styles.cards}>
          {items.map((item) => (
            <IncidentCard key={item.row.ref} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}

function IncidentCard({ item }: { item: IncidentToClose }) {
  const { row } = item;
  const title = typeof row.fields.Title === 'string' && row.fields.Title.trim() ? row.fields.Title.trim() : 'Untitled incident';
  const unit = typeof row.fields.BusinessUnit === 'string' && row.fields.BusinessUnit.trim() ? row.fields.BusinessUnit.trim() : 'No unit recorded';
  const readied = item.readiedBy
    ? `Readied by ${item.readiedByYou ? 'you' : item.readiedBy}${item.readiedAt ? ` on ${formatInstant(item.readiedAt)}` : ''}`
    : 'Readied by: not on the trail';
  return (
    <li className={styles.card} aria-labelledby={`itc-${row.id}`}>
      <div className={styles.cardHead}>
        <span className={styles.ref}>{row.ref}</span>
        <span id={`itc-${row.id}`} className={styles.cardTitle}>
          {title}
        </span>
        {item.ready ? (
          <span className={styles.readyTag}>Ready to close</span>
        ) : item.open.length > 0 ? (
          <span className={styles.openTag}>{plural(item.open.length, 'check')} open</span>
        ) : null}
      </div>
      <p className={styles.meta}>
        {unit} · {readied}
      </p>
      <IncidentChecks items={item.checks} label={`Checks for ${row.ref}`} />
      {item.readiedByYou && <p className={styles.note}>{SELF_READIED_TEXT}</p>}
      {item.readiedBy === null && <p className={styles.note}>{READIER_UNKNOWN_TEXT}</p>}
      <div className={styles.actions}>
        <Link className={styles.open} to={`/record/${row.ref}#panel-closure`} aria-label={`Open ${row.ref} to close`}>
          Open to close
        </Link>
      </div>
    </li>
  );
}
